/*
 * @Description: Why choose FalcoCut AI Video Editor section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '\u26A1\uFE0F',
    title: 'Simple to Learn',
    description:
      'Complex steps are replaced with clear, guided actions that make editing feel approachable. Even first\u2011time creators can produce clean, polished videos without a long learning curve.',
  },
  {
    icon: '\uD83C\uDF10',
    title: 'High\u2011Quality Output',
    description:
      'Every edit is processed with consistent precision, helping videos look clean and polished without extra effort. Deliver pro\u2011level results even on tight timelines or smaller budgets.',
  },
  {
    icon: '\uD83E\uDDE9',
    title: 'Faster Result',
    description:
      'Trimming, pacing, and formatting happen quickly, turning long edits into short tasks. Staying active across platforms becomes far more realistic when the process moves this fast.',
  },
  {
    icon: '\uD83D\uDD12',
    title: 'Privacy You Can Trust',
    description:
      'Your files stay protected over the editing process with strict handling and controlled access. Teams can work confidently knowing their content remains secure from upload to export.',
  },
  {
    icon: '\u2728',
    title: 'Versatility for Any Workflow',
    description:
      'The editor adapts to different content types, from training clips to social posts and product demos. Teams can switch between tasks easily without changing tools or rebuilding their setup.',
  },
  {
    icon: '\uD83D\uDCB0',
    title: 'Budget\u2011Friendly Editing',
    description:
      'Even small teams can manage video creation without heavy software expenses. The low cost makes it easy to stay active with content throughout the week.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut AI Video Editor
        </h2>

        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut makes video editing simple, fast, and affordable for everyone. It
          removes the stress of learning complex tools and helps teams stay consistent
          across platforms. With our video editor, more videos get finished with less
          effort and fewer resources.
        </p>

        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
